import React, { useState, useEffect } from "react";
import { 
    Grid, 
    Card, 
    InputLabel, 
    Icon, 
    CircularProgress, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    TextField, 
    Box, 
    Button,
    Chip,
    Tooltip
} from "@mui/material";
import { toDateTime } from "utils/DateUtils";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSelect from "components/MDSelect";
import MDInput from "components/MDInput";
import MDSnackbar from "components/MDSnackbar";
import { get, postNoResponse, postUploadNoResponse } from "utils/WebService";
import { useMaterialUIController } from "context";
import { useUser } from "context/UserContext";

export default function DadosCarteira({ 
    recordId, 
    preloadedDevedor, 
    selectedFicha,
    onSelectFicha,
    selectedFichaInitial,
    isViewOnly = false, 
    closeCreateEdit, 
    onDataUpdated 
}) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const { userPerfils } = useUser();

    const isGestorAdminOuCoord = userPerfils && userPerfils.some(p => {
        const name = (typeof p === 'string' ? p : (p?.nome || '')).toLowerCase();
        const tipo = typeof p === 'object' ? p?.tipoPerfil : null;
        return tipo === 1 || tipo === 3 || tipo === 4 || tipo === 6 || 
               name.includes("admin") || name.includes("gestor") || name.includes("coord") || name.includes("suporte");
    });

    const isAdminOrSuporte = userPerfils && userPerfils.some(p => {
        const name = (typeof p === 'string' ? p : (p?.nome || '')).toLowerCase();
        const tipo = typeof p === 'object' ? p?.tipoPerfil : null;
        return tipo === 1 || tipo === 3 || tipo === 6 || 
               name.includes("admin") || name.includes("suporte");
    });

    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [fichas, setFichas] = useState([]);
    const [selectedFichaIndex, setSelectedFichaIndex] = useState(0);

    const [formValues, setFormValues] = useState({
        statusId: "",
        usuarioId: "",
        marcacoesIds: [],
        observacao: ""
    });

    const [statusOptions, setStatusOptions] = useState([]);
    const [usuariosOptions, setUsuariosOptions] = useState([]);
    const [marcacoesOptions, setMarcacoesOptions] = useState([]);

    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [sbMessage, setSbMessage] = useState("");

    // Estados de Devolução
    const [openDevolverModal, setOpenDevolverModal] = useState(false);
    const [devolverObs, setDevolverObs] = useState("");
    const [devolverFile, setDevolverFile] = useState(null);
    const [isSubmittingDevolucao, setIsSubmittingDevolucao] = useState(false);
    const [confirmAction, setConfirmAction] = useState({ open: false, title: "", message: "", onConfirm: null });

    const loadOptions = () => {
        get("/status-ficha/list-all").then(res => {
            const list = res?.list || res?.data || (Array.isArray(res) ? res : []);
            setStatusOptions(list.sort((a, b) => String(a.ccNome || a.nome || "").localeCompare(String(b.ccNome || b.nome || ""))));
        }).catch(() => {});

        get("/users/list-all-operators").then(res => {
            setUsuariosOptions(res?.list || res?.data || (Array.isArray(res) ? res : []));
        }).catch(() => {});

        get("/marcacoes").then(res => {
            let list = res?.list || res?.data || (Array.isArray(res) ? res : []);
            setMarcacoesOptions(list.filter(m => m.cvAlvo === 1 || m.cvAlvo === 3));
        }).catch(() => {});
    };

    const processDevedorData = (data) => {
        if (!data) return;
        let list = [];
        if (data.fichas && Array.isArray(data.fichas) && data.fichas.length > 0) {
            const valid = data.fichas.filter(f => {
                if (!isGestorAdminOuCoord && f.pertenceAMinhaEquipe === false) return false;
                return true;
            });
            list = valid.length > 0 ? valid : data.fichas;
        }

        if (list.length === 0) {
            list = [{
                rawId: data.unencryptedId || data.id,
                id: data.id,
                nomeCarteira: data.nomeCarteira || "Carteira Principal",
                idStatusFicha: data.idStatusFicha || data.statusId || "",
                nomeStatusFicha: data.statusFichaNome || data.nomeStatusFicha || "",
                idUsuarioDono: data.idUsuario || data.idUsuarioDono || "",
                nomeUsuarioDono: data.nomeUsuario || data.nomeUsuarioDono || "",
                marcacoesIds: data.marcacoesIds || [],
                observacao: data.observacao || "",
                situacao: data.situacao,
                nomeCampanha: data.nomeCampanha,
                enquadramentoCampanha: data.enquadramentoCampanha,
                dataDevolucao: data.dataDevolucao,
                nomeUsuarioDevolucao: data.nomeUsuarioDevolucao
            }];
        }

        setFichas(list);
    };

    const loadDevedorFichas = () => {
        if (!recordId) return;
        setLoading(true);
        get(`/devedor/by-id/${recordId}`)
            .then(data => {
                processDevedorData(data);
            })
            .catch(err => console.error("Erro ao carregar fichas em DadosCarteira", err))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadOptions();
        if (preloadedDevedor) {
            processDevedorData(preloadedDevedor);
        }
        loadDevedorFichas();

        const handleReload = () => loadDevedorFichas();
        window.addEventListener("devedorStatusUpdated", handleReload);
        return () => window.removeEventListener("devedorStatusUpdated", handleReload);
    }, [recordId]);

    useEffect(() => {
        if (preloadedDevedor) {
            processDevedorData(preloadedDevedor);
        }
    }, [preloadedDevedor]);

    useEffect(() => {
        const target = selectedFicha || selectedFichaInitial;
        if (target && fichas.length > 0) {
            const idx = fichas.findIndex(f => 
                (f.rawId && target.rawId && String(f.rawId) === String(target.rawId)) ||
                (f.id && target.id && f.id === target.id)
            );
            if (idx >= 0 && idx !== selectedFichaIndex) {
                setSelectedFichaIndex(idx);
            }
        }
    }, [selectedFicha, selectedFichaInitial, fichas]);

    const activeFicha = fichas.length > 0 ? fichas[selectedFichaIndex] || fichas[0] : null;

    useEffect(() => {
        if (activeFicha) {
            setFormValues({
                statusId: activeFicha.idStatusFicha || activeFicha.statusId || "",
                usuarioId: activeFicha.idUsuarioDono || activeFicha.idUsuario || "",
                marcacoesIds: activeFicha.marcacoesIds || [],
                observacao: activeFicha.observacao || ""
            });
        }
    }, [activeFicha]);

    const handleSave = () => {
        if (!activeFicha) return;
        setIsSaving(true);
        const payload = {
            carteiraId: activeFicha.idCarteira || activeFicha.carteiraId || null,
            statusId: formValues.statusId || null,
            usuarioId: formValues.usuarioId || null,
            marcacoesIds: formValues.marcacoesIds || [],
            observacao: formValues.observacao || ""
        };

        const targetId = activeFicha.rawId || activeFicha.id;
        postNoResponse(`/devedor/carteira/update/${targetId}`, payload)
            .then(() => {
                setSbMessage("Dados da carteira salvos com sucesso!");
                setSuccessSB(true);
                loadDevedorFichas();
                window.dispatchEvent(new Event("devedorStatusUpdated"));
                if (onDataUpdated) onDataUpdated();
            })
            .catch(err => {
                console.error("Erro ao salvar dados da carteira", err);
                setSbMessage("Erro ao salvar os dados da carteira.");
                setErrorSB(true);
            })
            .finally(() => setIsSaving(false));
    };

    const handleDevolverDireto = () => {
        const targetId = activeFicha?.rawId || activeFicha?.id || recordId;
        const fichaDesc = activeFicha?.rawId ? `Ficha #${activeFicha.rawId}` : (activeFicha?.nomeCarteira || "desta carteira");
        setConfirmAction({
            open: true,
            title: "Devolver Ficha",
            message: `Deseja realmente devolver a ${fichaDesc}?`,
            onConfirm: async () => {
                try {
                    await postNoResponse(`/devedor/devolver/${targetId}`, {});
                    setSbMessage("Ficha devolvida com sucesso!");
                    setSuccessSB(true);
                    loadDevedorFichas();
                    window.dispatchEvent(new Event("devedorStatusUpdated"));
                    if (onDataUpdated) onDataUpdated();
                } catch (e) {
                    setSbMessage("Erro ao devolver ficha.");
                    setErrorSB(true);
                }
            }
        });
    };

    const handleReativar = () => {
        const targetId = activeFicha?.rawId || activeFicha?.id || recordId;
        const fichaDesc = activeFicha?.rawId ? `Ficha #${activeFicha.rawId}` : (activeFicha?.nomeCarteira || "desta carteira");
        setConfirmAction({
            open: true,
            title: "Reativar Ficha",
            message: `Deseja realmente reativar a ${fichaDesc}?`,
            onConfirm: async () => {
                try {
                    await postNoResponse(`/devedor/reativar/${targetId}`, {});
                    setSbMessage("Ficha reativada com sucesso!");
                    setSuccessSB(true);
                    loadDevedorFichas();
                    window.dispatchEvent(new Event("devedorStatusUpdated"));
                    if (onDataUpdated) onDataUpdated();
                } catch (e) {
                    setSbMessage("Erro ao reativar ficha.");
                    setErrorSB(true);
                }
            }
        });
    };

    const isFichaDevolvida = activeFicha?.situacao === 1;

    return (
        <MDBox>
            <Card sx={{ 
                p: 2.5, 
                backgroundColor: darkMode ? "#4B4F5C" : "#ffffff", 
                border: `1px solid ${isFichaDevolvida ? "#ef5350" : (darkMode ? "rgba(255,255,255,0.12)" : "#e0e0e0")}`,
                boxShadow: "none"
            }}>
                {loading ? (
                    <MDBox display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={30} />
                    </MDBox>
                ) : (
                    <Grid container spacing={2.5}>

                        {/* Se a ficha estiver devolvida e houver apenas 1 ficha, mostra o aviso de Devolvida de forma compacta */}
                        {fichas.length <= 1 && isFichaDevolvida && (
                            <Grid item xs={12}>
                                <Chip 
                                    label={
                                        activeFicha?.dataDevolucao 
                                            ? `FICHA DEVOLVIDA EM ${toDateTime(activeFicha.dataDevolucao)}${activeFicha?.nomeUsuarioDevolucao ? ` (${activeFicha.nomeUsuarioDevolucao})` : ""}` 
                                            : "FICHA DEVOLVIDA"
                                    } 
                                    color="error" 
                                    size="small" 
                                    sx={{ fontWeight: "bold", fontSize: "10px" }} 
                                />
                            </Grid>
                        )}

                        {/* Propriedade (Responsável) */}
                        <Grid item xs={12} sm={4}>
                            <InputLabel sx={{ px: 1, pb: 0.5, fontSize: "12px", color: darkMode ? "#ddd" : "#555" }}>
                                Propriedade (Responsável)
                            </InputLabel>
                            <MDSelect
                                isMulti={false}
                                getOptionValue={(option) => option.id}
                                getOptionLabel={(option) => option.nome}
                                placeholder="Selecione o responsável..."
                                value={usuariosOptions.find(u => String(u.id) === String(formValues.usuarioId)) || (activeFicha?.nomeUsuarioDono || activeFicha?.nomeUsuario ? { id: activeFicha.idUsuarioDono || activeFicha.idUsuario, nome: activeFicha.nomeUsuarioDono || activeFicha.nomeUsuario } : null)}
                                options={usuariosOptions}
                                onChange={(val) => setFormValues(prev => ({ ...prev, usuarioId: val ? val.id : "" }))}
                                isDisabled={!isGestorAdminOuCoord || isViewOnly}
                            />
                        </Grid>

                        {/* Status da Ficha */}
                        <Grid item xs={12} sm={4}>
                            <InputLabel sx={{ px: 1, pb: 0.5, fontSize: "12px", color: darkMode ? "#ddd" : "#555" }}>
                                Status da Ficha
                            </InputLabel>
                            <MDSelect
                                isMulti={false}
                                getOptionValue={(option) => option.id}
                                getOptionLabel={(option) => option.ccNome || option.nome}
                                placeholder="Selecione o status..."
                                value={statusOptions.find(s => String(s.id) === String(formValues.statusId)) || (activeFicha?.nomeStatusFicha || activeFicha?.statusFichaNome ? { id: activeFicha.idStatusFicha, ccNome: activeFicha.nomeStatusFicha || activeFicha.statusFichaNome } : null)}
                                options={statusOptions}
                                onChange={(val) => setFormValues(prev => ({ ...prev, statusId: val ? val.id : "" }))}
                                isDisabled={isViewOnly}
                            />
                        </Grid>

                        {/* Marcação Especial */}
                        <Grid item xs={12} sm={4}>
                            <InputLabel sx={{ px: 1, pb: 0.5, fontSize: "12px", color: darkMode ? "#ddd" : "#555" }}>
                                Marcação Especial
                            </InputLabel>
                            <MDSelect
                                isMulti={true}
                                getOptionValue={(option) => option.cvId}
                                getOptionLabel={(option) => option.ccNome}
                                placeholder="Selecione as marcações..."
                                value={marcacoesOptions.filter(m => formValues.marcacoesIds && formValues.marcacoesIds.includes(m.cvId))}
                                options={marcacoesOptions}
                                onChange={(selected) => setFormValues(prev => ({ ...prev, marcacoesIds: selected ? selected.map(s => s.cvId) : [] }))}
                                isDisabled={isViewOnly}
                            />
                        </Grid>

                        {/* Observação da Carteira */}
                        <Grid item xs={12}>
                            <InputLabel sx={{ px: 1, pb: 0.5, fontSize: "12px", color: darkMode ? "#ddd" : "#555" }}>
                                Observação da Carteira
                            </InputLabel>
                            <MDInput
                                fullWidth
                                multiline
                                rows={4}
                                placeholder="Observações operacionais específicas desta carteira..."
                                value={formValues.observacao}
                                onChange={(e) => setFormValues(prev => ({ ...prev, observacao: e.target.value }))}
                                inputProps={{
                                    readOnly: isViewOnly
                                }}
                            />
                        </Grid>

                        {/* Ações: Salvar e Devolução */}
                        <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mt={1}>
                            <MDBox display="flex" gap={1.5} alignItems="center">
                                {isFichaDevolvida && isAdminOrSuporte && (
                                    <MDButton 
                                        variant="gradient" 
                                        color="success" 
                                        onClick={handleReativar}
                                        disabled={isSaving}
                                    >
                                        <Icon sx={{ mr: 0.5 }}>restore</Icon> Reativar Ficha
                                    </MDButton>
                                )}

                                {!isFichaDevolvida && (
                                    isAdminOrSuporte ? (
                                        <MDButton 
                                            variant="gradient" 
                                            color="error" 
                                            onClick={handleDevolverDireto}
                                            disabled={isSaving}
                                        >
                                            <Icon sx={{ mr: 0.5 }}>assignment_return</Icon> Devolver Ficha
                                        </MDButton>
                                    ) : (
                                        <MDButton 
                                            variant="gradient" 
                                            color="warning" 
                                            onClick={() => setOpenDevolverModal(true)}
                                            disabled={isSaving}
                                        >
                                            <Icon sx={{ mr: 0.5 }}>assignment_return</Icon> Solicitar Devolução
                                        </MDButton>
                                    )
                                )}
                            </MDBox>

                            <MDButton 
                                variant="gradient" 
                                color="info" 
                                onClick={handleSave} 
                                disabled={isSaving || isViewOnly}
                            >
                                {isSaving ? <CircularProgress size={18} color="inherit" /> : "Salvar Dados da Carteira"}
                            </MDButton>
                        </Grid>
                    </Grid>
                )}
            </Card>

            {/* Modal de Solicitação de Devolução (Operador) */}
            <Dialog 
                open={openDevolverModal} 
                onClose={() => !isSubmittingDevolucao && setOpenDevolverModal(false)} 
                maxWidth="sm" 
                fullWidth
            >
                <DialogTitle>Solicitar Devolução da Carteira</DialogTitle>
                <DialogContent>
                    <Box mt={2}>
                        <TextField
                            label="Motivo / Justificativa da Devolução"
                            multiline
                            rows={4}
                            fullWidth
                            value={devolverObs}
                            onChange={(e) => setDevolverObs(e.target.value)}
                            disabled={isSubmittingDevolucao}
                            required
                        />
                    </Box>
                    <Box mt={2}>
                        <MDTypography variant="caption" fontWeight="bold">Anexar Documento Comprobatório (Opcional)</MDTypography>
                        <input
                            type="file"
                            onChange={(e) => setDevolverFile(e.target.files[0])}
                            disabled={isSubmittingDevolucao}
                            style={{ display: "block", marginTop: "8px" }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDevolverModal(false)} disabled={isSubmittingDevolucao}>Cancelar</Button>
                    <Button 
                        onClick={async () => {
                            if (!devolverObs) {
                                alert("Preencha o motivo/justificativa da devolução.");
                                return;
                            }
                            setIsSubmittingDevolucao(true);
                            try {
                                const targetId = activeFicha?.rawId || activeFicha?.id || recordId;
                                await postNoResponse(`/devedor/solicitar-devolucao/${targetId}`, { observacao: devolverObs });
                                if (devolverFile) {
                                    const formData = new FormData();
                                    formData.append("file", devolverFile);
                                    formData.append("devedorId", recordId);
                                    await postUploadNoResponse(`/documents/devedor/upload`, formData);
                                }
                                setOpenDevolverModal(false);
                                setSbMessage("Solicitação de devolução enviada com sucesso!");
                                setSuccessSB(true);
                                setIsSubmittingDevolucao(false);
                                if (closeCreateEdit) closeCreateEdit();
                            } catch (e) {
                                setSbMessage("Erro ao enviar solicitação de devolução.");
                                setErrorSB(true);
                                setIsSubmittingDevolucao(false);
                            }
                        }} 
                        variant="contained" 
                        color="primary" 
                        disabled={isSubmittingDevolucao}
                    >
                        {isSubmittingDevolucao ? <CircularProgress size={16} color="inherit" /> : "Enviar Solicitação"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo de Confirmação */}
            <Dialog 
                open={confirmAction.open}
                onClose={() => setConfirmAction({ open: false, title: "", message: "", onConfirm: null })}
                PaperProps={{
                    sx: {
                        background: darkMode ? "#4B4F5C" : "#fff",
                        color: darkMode ? "#fff" : "inherit"
                    }
                }}
            >
                <DialogTitle>{confirmAction.title}</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2" color={darkMode ? "white" : "text"}>
                        {confirmAction.message}
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmAction({ open: false, title: "", message: "", onConfirm: null })} color="inherit">
                        Cancelar
                    </Button>
                    <Button 
                        variant="contained" 
                        color="primary"
                        onClick={async () => {
                            const fn = confirmAction.onConfirm;
                            setConfirmAction({ open: false, title: "", message: "", onConfirm: null });
                            if (fn) await fn();
                        }}
                    >
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

            <MDSnackbar
                color="success"
                icon="check"
                title="Sucesso"
                content={sbMessage}
                dateTime=""
                open={successSB}
                onClose={() => setSuccessSB(false)}
                close={() => setSuccessSB(false)}
                bgWhite
            />
            <MDSnackbar
                color="error"
                icon="warning"
                title="Erro"
                content={sbMessage}
                dateTime=""
                open={errorSB}
                onClose={() => setErrorSB(false)}
                close={() => setErrorSB(false)}
                bgWhite
            />
        </MDBox>
    );
}
